import { FC, useCallback, useEffect, useMemo } from 'react';
import { Form, Formik } from 'formik';
import { GenericLoading } from 'components/ui/Loader';
import { ConfirmationOverlay } from 'components/feedback/ConfirmationOverlay';
import {
  useBorrowerGlobalInfos,
  useBorrowerId
} from 'features/subscription-process/contractualization-process/store';
import { useBorrowerInsuredWithDocuments } from './useBorrowerInsuredWithDocuments';
import { buildBorrowerInsuredWithDocumentsValidationSchema } from './borrowerInsuredWithDocuments.config';
import {
  isMncapEmprunteurQuoteLabel,
  selectQuoteLabelForBorrower
} from './borrowerInsuredWithDocuments.selectors';
import { BorrowerInsuredWithDocumentsLayout } from './BorrowerInsuredWithDocumentsLayout';

interface BorrowerInsuredWithDocumentsFormProps {
  personId: string;
  onSuccess?: () => void;
  onIdentityStatusChanged?: () => void;
  /** Id du projet fourni par l'ouvrant (prioritaire sur l'URL) — fiable en panneau. */
  borrowerProjectId?: string;
}

/**
 * Remonte la validité Formik au hook via un effet (jamais pendant le rendu) : évite le
 * warning React « Cannot update a component while rendering a different component ».
 */
const ValidationTracker: FC<{
  isValid: boolean;
  onValidationChange: (isValid: boolean) => void;
}> = ({ isValid, onValidationChange }) => {
  useEffect(() => {
    onValidationChange(isValid);
  }, [isValid, onValidationChange]);
  return null;
};

/**
 * Composant autonome pour éditer un assuré emprunteur avec documents.
 * - Pas de recherche de personne (édition uniquement)
 * - 3 onglets : Informations, Situation, Documents
 */
export const BorrowerInsuredWithDocumentsForm: FC<
  BorrowerInsuredWithDocumentsFormProps
> = ({ personId, onSuccess, onIdentityStatusChanged, borrowerProjectId }) => {
  const {
    formikRef,
    initialValues,
    isLoading,
    isSubmitting,
    handleSubmit,
    handleValidationChange,
    isRiskProfessionConfirmOpen,
    confirmRiskProfessionChange,
    cancelRiskProfessionChange,
    blockingState,
    closeBlockingStateModal
  } = useBorrowerInsuredWithDocuments(personId, onSuccess, borrowerProjectId);

  const noop = useCallback(() => {
    // ConfirmationOverlay appelle onConfirm puis onClose ; ici on veut juste
    // que onClose (backdrop / Esc / clic Compris) déclenche closeBlockingStateModal.
  }, []);

  const globalInfos = useBorrowerGlobalInfos();
  const borrowerId = useBorrowerId();
  const quoteLabel = useMemo(
    () => selectQuoteLabelForBorrower(globalInfos, borrowerId),
    [globalInfos, borrowerId]
  );
  const requiresBrokerConfirmation = useMemo(
    () => isMncapEmprunteurQuoteLabel(quoteLabel),
    [quoteLabel]
  );

  const validationSchema = useMemo(
    () => buildBorrowerInsuredWithDocumentsValidationSchema({ requiresBrokerConfirmation }),
    [requiresBrokerConfirmation]
  );

  if (isLoading || !initialValues) {
    return <GenericLoading loading isParentSized />;
  }

  return (
    <div className="relative">
      {isSubmitting && <GenericLoading loading isParentSized />}
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        validateOnMount
        enableReinitialize
        onSubmit={handleSubmit}
        innerRef={formikRef}
      >
        {({ isValid }) => (
          <Form>
            <ValidationTracker
              isValid={isValid}
              onValidationChange={handleValidationChange}
            />
            <BorrowerInsuredWithDocumentsLayout
              personId={personId}
              onIdentityStatusChanged={onIdentityStatusChanged}
              requiresBrokerConfirmation={requiresBrokerConfirmation}
            />
          </Form>
        )}
      </Formik>

      {/*
        Garde-fou pré-submit : profession à risque passe de "Aucune" à autre.
        Texte volontairement explicite — le devis en cours sera annulé côté back.
      */}
      <ConfirmationOverlay
        isOpen={isRiskProfessionConfirmOpen}
        onClose={cancelRiskProfessionChange}
        onConfirm={confirmRiskProfessionChange}
        title="Profession à risque"
        description="Cette profession à risque n'est pas couverte par l'assureur. Si vous validez, le devis sera annulé et il faudra recommencer une nouvelle simulation. Continuer ?"
        confirmLabel="COMMON.CONTINUE"
        cancelLabel="COMMON.CANCEL"
        variant="warning"
        isLoading={isSubmitting}
      />

      {/*
        Modale d'info post-submit : le back a annulé un devis. On force
        l'acquittement avant de rafraîchir et fermer le panneau.
      */}
      <ConfirmationOverlay
        isOpen={blockingState !== null}
        onClose={closeBlockingStateModal}
        onConfirm={noop}
        title="Devis annulé"
        description={blockingState?.message ?? ''}
        confirmLabel="COMMON.UNDERSTOOD"
        variant="warning"
        hideCancel
      />
    </div>
  );
};
